import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const Activities = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const activities = [
    {
      id: "trainings",
      title: t("activities.types.trainings.title"),
      description: t("activities.types.trainings.description"),
      action: t("activities.types.trainings.action"),
      color: "from-teal-500 to-teal-600",
      bgColor: "from-teal-50 to-teal-100",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
    },
    {
      id: "workshops",
      title: t("activities.types.workshops.title"),
      description: t("activities.types.workshops.description"),
      action: t("activities.types.workshops.action"),
      color: "from-lime-400 to-lime-500",
      bgColor: "from-lime-50 to-lime-100",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
          />
        </svg>
      ),
    },
    {
      id: "campaigns",
      title: t("activities.types.campaigns.title"),
      description: t("activities.types.campaigns.description"),
      action: t("activities.types.campaigns.action"),
      color: "from-yellow-400 to-yellow-500",
      bgColor: "from-yellow-50 to-yellow-100",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
          />
        </svg>
      ),
    },
    {
      id: "youthInitiatives",
      title: t("activities.types.youthInitiatives.title"),
      description: t("activities.types.youthInitiatives.description"),
      action: t("activities.types.youthInitiatives.action"),
      color: "from-teal-500 via-lime-400 to-yellow-400",
      bgColor: "from-teal-50 via-lime-50 to-yellow-50",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="activities"
      className="py-12 relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50"
    >
      {/* Modern background pattern */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f1f5f9' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100/30 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-emerald-100/30 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        {/* Modern section header */}
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold mb-6">
            <svg
              className="w-4 h-4 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Our Activities
          </div>
          <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent mb-6">
            {t("activities.title")}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Discover our diverse range of activities designed to empower and
            inspire
          </p>
        </div>
        {/* Floating notice banner */}
        <div
          className={`fixed bottom-4 right-4 z-40 transition-all duration-1000 delay-300 ${
            isVisible
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-full"
          }`}
        >
          <div className="relative rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-2xl border border-white/20 overflow-hidden w-80 sm:w-96 md:max-w-sm bg-[#14b8a6]">
            <div className="relative flex items-center">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-2 sm:ml-3 flex-1 min-w-0">
                <p className="text-white font-semibold text-xs sm:text-sm leading-tight">
                  {t("activities.notice")}
                </p>
              </div>
            </div>
            {/* Animated border */}
            <div className="absolute inset-0 rounded-xl sm:rounded-2xl border border-white/30 animate-pulse"></div>
          </div>
        </div>
        {/* Modern activities grid */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 transition-all duration-1000 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {activities.map((activity, index) => (
            <div
              key={activity.id}
              className="group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/20 transition-all duration-500 hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-3 overflow-hidden"
              style={{
                background: `linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)`,
              }}
            >
              {/* Animated background gradient */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${activity.bgColor} opacity-0 group-hover:opacity-20 transition-all duration-500 rounded-3xl`}
              ></div>

              {/* Floating elements */}
              <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-white/20 to-transparent rounded-full blur-xl group-hover:scale-150 transition-all duration-700"></div>
              <div className="absolute bottom-4 left-4 w-16 h-16 bg-gradient-to-tr from-white/10 to-transparent rounded-full blur-lg group-hover:scale-125 transition-all duration-700 delay-100"></div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon and title section */}
                <div className="flex items-start mb-8">
                  <div
                    className={`w-20 h-20 bg-gradient-to-br ${activity.color} rounded-3xl flex items-center justify-center text-white mr-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-2xl group-hover:shadow-3xl`}
                  >
                    {activity.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-gray-800 transition-colors duration-300 mb-2">
                      {activity.title}
                    </h3>
                    <div className="w-12 h-1 bg-gradient-to-r from-teal-500 via-lime-400 to-yellow-400 rounded-full"></div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-lg leading-relaxed mb-8 text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                  {activity.description}
                </p>

                {/* Decorative element */}
                <div className="flex justify-end mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <div className="w-3 h-3 bg-gradient-to-br from-teal-400 via-lime-400 to-yellow-400 rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Action button at the bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-8 pt-0">
                <button
                  disabled={
                    activity.id === "workshops" ||
                    activity.id === "campaigns" ||
                    activity.id === "youthInitiatives"
                  }
                  onClick={() => {
                    if (activity.id === "trainings") {
                      navigate("/trainings");
                    }
                  }}
                  className={`group/btn relative inline-flex items-center font-semibold transition-all duration-300 px-6 py-3 rounded-2xl overflow-hidden ${
                    activity.id === "workshops" ||
                    activity.id === "campaigns" ||
                    activity.id === "youthInitiatives"
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-sm"
                      : activity.id === "trainings"
                      ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700 shadow-lg hover:shadow-xl"
                      : "bg-gradient-to-r from-teal-500 via-lime-400 to-yellow-400 text-white hover:from-teal-600 hover:via-lime-500 hover:to-yellow-500 shadow-lg hover:shadow-xl"
                  }`}
                >
                  <span className="relative z-10 mr-2">{activity.action}</span>
                  <svg
                    className={`w-5 h-5 transition-transform duration-300 ${
                      activity.id === "workshops" ||
                      activity.id === "campaigns" ||
                      activity.id === "youthInitiatives"
                        ? ""
                        : "group-hover/btn:translate-x-1"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                  {!(
                    activity.id === "workshops" ||
                    activity.id === "campaigns" ||
                    activity.id === "youthInitiatives"
                  ) && (
                    <div className="absolute inset-0 bg-white/20 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                  )}
                </button>
              </div>

              {/* Modern border effect */}
              <div className="absolute inset-0 rounded-3xl border border-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
          ))}
        </div>
        {/* Bottom decorative element */}
      </div>
    </section>
  );
};

export default Activities;
